/* eslint-disable no-console */
// In production, we register a service worker to serve assets from local cache.

import { diff, valid } from "semver";

import { appUpdateStatus, appUpdateType } from "constants/system";

import { DEBUG_SERVICE_WORKER, debugServiceWorkerLog } from "lib/errors";

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on the "N+1" visit to a page, since previously
// cached resources are updated in the background.

// To learn more about the benefits of this model, read https://goo.gl/KwvDNy.
// This link also includes instructions on opting out of this behavior.

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === "[::1]" ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
    ),
);

// Track the update checker interval to prevent race conditions
let updateCheckerInterval: ReturnType<typeof setInterval> | null = null;
// Default check for updates every 5 minutes
const intervalMs = Number(process.env.REACT_APP_UPDATE_CHECK_MS) || 300000;

export function clearUpdateCheckerInterval(): void {
  if (updateCheckerInterval) {
    clearInterval(updateCheckerInterval);
    updateCheckerInterval = null;
  }
}

export function checkForSWUpdates(): void {
  if (!navigator.serviceWorker) return;
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      debugServiceWorkerLog("Checking for update");
      registration.update().then(() => {
        debugServiceWorkerLog("Finished update");
      });
    });
  });
}

function registerValidSW(
  swUrl: string,
  onUpdate: (status: string) => void,
): void {
  debugServiceWorkerLog("registerValidSW called");
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      debugServiceWorkerLog("setting interval");

      // Clear any existing interval to prevent race conditions
      clearUpdateCheckerInterval();

      updateCheckerInterval = setInterval(() => {
        debugServiceWorkerLog("Checking for updates...");
        registration.update().then(
          () => debugServiceWorkerLog("finished checking"),
          e => {
            debugServiceWorkerLog(`Update check failed: ${e}`);
            if (e.msg === "Not found") {
              clearUpdateCheckerInterval();
            }
          },
        );
      }, intervalMs);

      registration.onupdatefound = async () => {
        debugServiceWorkerLog("registration.onupdatefound called");
        const installingWorker = registration.installing;
        if (!installingWorker) return;

        installingWorker.onstatechange = async () => {
          if (installingWorker.state === "installed") {
            debugServiceWorkerLog("service worker installed");
            if (navigator.serviceWorker.controller) {
              debugServiceWorkerLog("service worker controller found");
              // At this point, the old content will have been purged and
              // the fresh content will have been added to the cache.
              // It's the perfect time to display a "New content is
              // available; please refresh." message in your web app

              const currentVersion = process.env.REACT_APP_SEMANTIC_VERSION;
              debugServiceWorkerLog(`Current app version: ${currentVersion}`);

              // According to workbox documentation we should be able to access the pre-cache
              // and in theory the new version of the app:
              // https://developer.chrome.com/docs/workbox/modules/workbox-precaching#reading_precached_assets_directly
              // However this did not work - cacheNames.precache => "workbox-precache-v2" which empty at this point
              // There is another caches which is populated "workbox-precache-<origin>" but that has stale data, i.e.
              // it stores the previous (current) version.  So instead we will just fetch the new version file directly

              let nextVersion: string | null = null;
              try {
                const response = await fetch("/version.json", {
                  cache: "no-store",
                });
                if (response.ok) {
                  const data = await response.json();
                  nextVersion = data?.version ?? null;
                }
              } catch (error) {
                console.error("Error fetching version.json:", error);
              }

              let updateType: string = appUpdateType.PATCH; // default release type
              if (nextVersion) {
                debugServiceWorkerLog(
                  `Fetched next app version: ${nextVersion} (Comparing with current version: ${currentVersion})`,
                );
                if (currentVersion) {
                  // Validate semver before calling diff
                  if (valid(currentVersion) && valid(nextVersion)) {
                    updateType =
                      diff(currentVersion, nextVersion) ?? updateType;
                  }
                }
              } else {
                console.warn("Could not fetch next app version");
              }

              debugServiceWorkerLog(`Update type: ${updateType}`);

              let updateStatus: string;

              switch (updateType) {
                // The default production release type
                case appUpdateType.PATCH: // e.g. 1.0.1 => 1.0.2
                  updateStatus = appUpdateStatus.REFRESH_OPTIONAL;
                  break;

                // Pre-release updates for non-prod e.g. sandbox will have this type
                case appUpdateType.PRERELEASE: // e.g. 1.0.1-pre.956ddcc7a => 1.0.1-pre.bcd4e2a82
                  updateStatus = appUpdateStatus.REFRESH_OPTIONAL;
                  break;

                // Invisible release for urgent fixes that we don't want the world to know about
                // Users will need to be be informed by support and instructed to manually refresh
                case appUpdateType.PREPATCH: // e.g. 1.0.1 => 1.0.2-pre.fixplacards
                  updateStatus = appUpdateStatus.INVISIBLE;
                  break;

                // Pre-release updates for non-prod e.g. sandbox will have this type, if we incremented
                // the minor version on the pre-release
                case appUpdateType.PREMINOR: // e.g. 2.0.1 => 2.1.0-pre.f11ea25ea
                  updateStatus = appUpdateStatus.REFRESH_OPTIONAL;
                  break;

                // Production minor release with in your face toast popup notification
                case appUpdateType.MINOR: // e.g. 2.0.1 => 2.1.0
                  updateStatus = appUpdateStatus.REFRESH_REQUIRED;
                  break;

                // Pre-release updates for non-prod e.g. sandbox will have this type, if we incremented
                // the minor version on the pre-release
                case appUpdateType.PREMAJOR: // e.g. 2.0.1 => 3.0.0-pre.f11ea25ea
                  updateStatus = appUpdateStatus.REFRESH_OPTIONAL;
                  break;

                // Production major forces user logout
                case appUpdateType.MAJOR: // e.g. 2.0.1 => 3.0.0
                  updateStatus = appUpdateStatus.LOGOUT_REQUIRED;
                  break;

                default:
                  updateStatus = appUpdateStatus.REFRESH_OPTIONAL;
              }
              onUpdate(updateStatus);

              debugServiceWorkerLog(
                "New content is available; please refresh.",
              );
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              debugServiceWorkerLog("Content is cached for offline use.");
            }
          }
        };
      };
    })
    .catch(error => {
      console.error("Error during service worker registration:", error);
    });
}

function checkValidServiceWorker(
  swUrl: string,
  onUpdate: (status: string) => void,
): void {
  debugServiceWorkerLog(`checkValidServiceWorker called - swUrl: ${swUrl}`);
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl)
    .then(response => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get("content-type");
      if (
        response.status === 404 ||
        (contentType && contentType.indexOf("javascript") === -1)
      ) {
        debugServiceWorkerLog("no service worker found - reload the page");
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        debugServiceWorkerLog("register valid service worker");
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, onUpdate);
      }
    })
    .catch(() => {
      console.log(
        "No internet connection found. App is running in offline mode.",
      );
    });
}

export function unregister(): void {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready.then(registration => {
      registration.unregister();
    });
  }
}

export default function register(onUpdate: (status: string) => void): void {
  if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
    console.log(
      `Service worker registering (debug logging: ${DEBUG_SERVICE_WORKER ? "enabled" : "disabled"};  Update Check Interval: ${intervalMs}ms); Current app version: ${process.env.REACT_APP_SEMANTIC_VERSION}`,
    );
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(
      process.env.PUBLIC_URL || "",
      window.location.href,
    );
    if (publicUrl.origin !== window.location.origin) {
      debugServiceWorkerLog(
        "service worker giving up - public URL origin differs from window origin",
      );
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebookincubator/create-react-app/issues/2374
      return;
    }

    const runRegistration = (): void => {
      debugServiceWorkerLog("Service worker: registration handler ran");
      const swUrl = `${process.env.PUBLIC_URL || ""}/sw-default.js`;

      // Don't use the service worker for the reports endpoint, these must be online
      // and it helps with sharing the reports on ios pwa.
      if (window.location.pathname.startsWith("/reports")) {
        debugServiceWorkerLog("unregistering service worker for reports url");
        unregister();
        return;
      }

      if (isLocalhost) {
        debugServiceWorkerLog("service worker isLocalHost");
        // This is running on localhost. Lets check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, onUpdate);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            "This web app is being served cache-first by a service " +
              "worker. To learn more, visit https://goo.gl/SC7cgQ",
          );
        });
      } else {
        debugServiceWorkerLog("registering valid service worker");
        // Is not local host. Just register service worker
        registerValidSW(swUrl, onUpdate);
      }
    };

    // Avoid race: register() is called from createStore callback, which can run
    // after window "load" has already fired. If load already happened, run now.
    if (document.readyState === "complete") {
      debugServiceWorkerLog(
        "Service worker: document ready state is complete, running registration",
      );
      runRegistration();
    } else {
      debugServiceWorkerLog(
        "Service worker: document ready state is not complete, adding load event listener",
      );
      window.addEventListener("load", runRegistration);
    }
  }
}
